import { HttpClient } from "@angular/common/http";
import { HttpHeaders } from "@angular/common/http";
import {
    AfterViewInit,
    Component,
    Input,
    OnInit,
    ViewChild,
} from "@angular/core";
import { NgForm } from "@angular/forms";
import { DialerService } from "dialer";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import { firstValueFrom, of, race, timer } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { AppState } from "../../core";
import { UserService } from "../../core/services/user.service";

@Component({
    selector: "app-dialer-client-issue-modal",
    templateUrl: "./dialer-client-issue-modal.component.html",
    styleUrls: ["./dialer-client-issue-modal.component.scss"],
})
export class DialerClientIssueModalComponent implements OnInit, AfterViewInit {
    static MODAL_ID = 20221222;

    static showModal(
        modalService: BsModalService,
        initialState: Partial<DialerClientIssueModalComponent>
    ) {
        return modalService.show(DialerClientIssueModalComponent, {
            ignoreBackdropClick: true,
            initialState,
            class: "dialer-client-issue-modal full-screen-modal",
            id: DialerClientIssueModalComponent.MODAL_ID,
        });
    }

    @ViewChild("issueReportForm") issueReportForm: NgForm;

    iProcessingCnt = 0;
    versionInfo: any;
    @Input() journalSince = "3 hours ago";
    @Input() journalUntil = "now";
    @Input() testTitle = "";
    issueDescription: string;
    errorMsg = "";

    constructor(
        public userService: UserService,
        public dialerService: DialerService,
        public httpClient: HttpClient,
        protected appState: AppState,
        protected modalRef: BsModalRef
    ) {}

    ngOnInit() {
        this.loadVersion();
    }

    ngAfterViewInit(): void {
        //this.loadJournalCtlLog();
        this.issueReportForm.form.markAsDirty();
    }

    async loadVersion() {
        this.iProcessingCnt++;
        try {
            this.versionInfo = await firstValueFrom(
                race(
                    this.httpClient
                        .get(this.dialerService.config.url + "app/version")
                        .pipe(
                            catchError((e) =>
                                of({ error: `${e.message || e}` })
                            )
                        ),
                    timer(8000).pipe(map((_) => ({ error: "Timed out" })))
                )
            );
            if (this.versionInfo?.error) {
                this.errorMsg = this.versionInfo.error.message;
            }
        } catch (e: any) {
            console.error(e);
            this.errorMsg = e.message;
        } finally {
            this.iProcessingCnt--;
        }
    }

    onCancel() {
        this.modalRef.hide();
    }

    exportProcessing = false;
    async exportIssueReport() {
        this.iProcessingCnt++;
        this.exportProcessing = true;
        this.errorMsg = "";
        try {
            if (this.issueReportForm.dirty) {
                await this.loadJournalCtlLog();
            }

            const vi = this.versionInfo;
            const user = this.appState.getUser();
            const reportHeader =
                `Version: ${vi?.version} ${vi?.git}\n` +
                `Process Start Time: ${vi?.process_start_time} (up ${vi?.uptime_minutes} minutes)\n` +
                `Created by ${user?.firstName} ${user?.lastName} (${
                    user?.email
                }) at ${new Date()}\n` +
                `Journalctl since '${this.journalSince}' until '${this.journalUntil}'\n\n`;

            const blob = new Blob([reportHeader + this.journalCtlLog], {
                type: "text/plain",
            });
            saveAs(blob, `dialer_issue_${this.testTitle||'report'}.txt`);
        } catch (e: any) {
            console.error(e);
            this.errorMsg = e.message;
        } finally {
            this.exportProcessing = false;
            this.iProcessingCnt--;
        }
    }

    journalCtlLog: any = "";
    journalctlProcessing = false;
    exportable = true;
    async loadJournalCtlLog() {
        this.iProcessingCnt++;
        this.journalctlProcessing = true;
        this.errorMsg = "";
        try {
            const headers = new HttpHeaders();
            headers.append("Content-Type", "text/plain");
            headers.append("Accept", "text/plain");
            this.journalCtlLog = await firstValueFrom(
                this.httpClient
                    .get(this.dialerService.config.url + "app/log", {
                        headers: headers,
                        responseType: "text",
                        params: {
                            since: this.journalSince,
                            until: this.journalUntil,
                        },
                    })
                    .pipe(
                        map((jsonString) => jsonString),
                        catchError((e) => {
                            this.errorMsg = e?.message || `${e}`;
                            return of({ error: `${e.message || e}` });
                        })
                    )
            );
            if ( this.journalCtlLog?.error ) {
                this.journalCtlLog = '';
                this.exportable = false;
            } else if ( ["-- No entries --", "Failed", "<!DOCTYPE html>"].some(
                    (outputText) => this.journalCtlLog.startsWith(outputText)
                )
            ) {
                this.exportable = false;
            } else {
                this.exportable = true;
            }
            this.issueReportForm.form.markAsPristine();
        } catch (e: any) {
            console.error(e);
            this.errorMsg = e.message;
        } finally {
            this.iProcessingCnt--;
            this.journalctlProcessing = false;
        }
    }
}
